import { useEffect, useState } from "react";
import { deepRead, readingCols } from "@/mocks/guatian";

const bodyParagraphs = [
  "在过去一周的舆论观察里，「反转」几乎是最高频的词。但若把镜头拉远会发现，绝大多数反转并非凭空出现，而是本就埋了三个前兆：传播跑在核实前、语境被层层折叠、以及「情绪优先于求证」的互动氛围。",
  "第一条线索是速度。当一条「爆点」在几分钟内跨越平台传播时，它携带的信息往往只是原事件的某一个切面。截图与标题本身就是一种裁剪，被传播的越多，距离原始语境的误差通常也越大。",
  "第二条线索是来源。真正可靠的信号并不总是「第一个发声」的，而往往是「愿意展示原始素材、且可被交叉验证」的。我们把这类账号称为高证据密度来源，它们通常拥有稳定的历史记录与完整的素材链。",
  "第三条，也是最重要的一条：在官方口径与多方证据出现之前，保持「待证实」的心态本身就是在维护真相。少下结论、多留悬念，未必是无趣的吃瓜，反而是对事实最基本的尊重。",
];

export default function DeepRead() {
  const [reading, setReading] = useState(false);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setReading(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const toneMap: Record<string, { bar: string; chip: string; hover: string }> = {
    primary: { bar: "bg-primary-500", chip: "bg-primary-100 text-primary-700", hover: "group-hover:text-primary-700" },
    accent: { bar: "bg-accent-500", chip: "bg-accent-100 text-accent-700", hover: "group-hover:text-accent-700" },
    secondary: { bar: "bg-secondary-500", chip: "bg-secondary-100 text-secondary-800", hover: "group-hover:text-secondary-700" },
    foreground: { bar: "bg-foreground-950", chip: "bg-background-200 text-foreground-700", hover: "group-hover:text-foreground-950" },
  };

  return (
    <section id="columns" className="border-y border-background-200 bg-background-100/60">
      <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-bold text-secondary-800">
              <i className="ri-book-open-line" aria-hidden="true"></i>
              阅读专区
            </div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              不止吃瓜，<span className="text-secondary-600">读懂舆情</span>
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-foreground-500">
              深读长文带你理解信息传播的规律，让每一次吃瓜都不白吃。
            </p>
          </div>
          <a
            href="#about"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-bold text-foreground-700 transition hover:border-secondary-400 hover:text-secondary-700"
          >
            关于我们的内容原则
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </a>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-5">
          {/* 深读长文 */}
          <article className="group grid overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:col-span-3 lg:grid-cols-2">
            <div className="relative h-52 w-full overflow-hidden md:h-64 lg:h-auto">
              <img
                src={deepRead.img}
                alt={deepRead.alt}
                className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col justify-between p-6 md:p-7">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary-100 px-2 py-1 text-xs font-bold text-secondary-800">
                    <i className="ri-bookmark-line" aria-hidden="true"></i>
                    深读
                  </span>
                  {deepRead.tags.slice(0, 2).map((t) => (
                    <span key={t} className="text-xs text-foreground-400"># {t}</span>
                  ))}
                </div>
                <h3 className="mt-3 text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                  {deepRead.title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-foreground-600">{deepRead.summary}</p>
              </div>
              <button
                onClick={() => setReading(true)}
                className="mt-5 inline-flex items-center gap-2 self-start whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-bold text-foreground-950 transition hover:bg-secondary-600"
              >
                开始精读全文
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </button>
            </div>
          </article>

          {/* 分类入口 */}
          <div className="grid grid-cols-2 gap-5 lg:col-span-2 lg:grid-cols-1">
            {readingCols.map((c) => {
              const t = toneMap[c.tone];
              return (
                <a
                  key={c.id}
                  href="#updates"
                  className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition hover:border-background-300"
                >
                  <span className={`mt-1 w-1.5 self-stretch rounded-full ${t.bar}`}></span>
                  <div className="min-w-0 flex-1">
                    <h3 className={`flex items-center gap-2 text-lg font-bold text-foreground-950 ${t.hover}`}>
                      {c.name}
                      <i className="ri-arrow-right-up-line text-sm opacity-0 transition group-hover:opacity-100" aria-hidden="true"></i>
                    </h3>
                    <p className="mt-1.5 text-sm text-foreground-500">{c.desc}</p>
                    <div className="mt-3 flex items-center gap-2">
                      <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${t.chip}`}>{c.count} 篇</span>
                      <span className="text-xs text-foreground-300">持续更新中</span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* 阅读弹层 */}
      {reading && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground-950/60 px-4 py-8 backdrop-blur-sm" onClick={() => setReading(false)}>
          <div
            className="gt-scrollbar relative w-full max-w-3xl overflow-hidden rounded-lg bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-44 w-full md:h-56">
              <img src={deepRead.img} alt={deepRead.alt} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-transparent"></div>
              <button
                onClick={() => setReading(false)}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-background-50/90 text-foreground-800 transition hover:bg-background-50"
                aria-label="关闭阅读"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>
            <div className="p-6 md:p-9">
              <h3 className="text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">{deepRead.title}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {deepRead.tags.map((t) => (
                  <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800"># {t}</span>
                ))}
              </div>
              <div className="mt-6 space-y-5">
                {bodyParagraphs.map((p, i) => (
                  <p key={i} className="text-[15px] leading-8 text-foreground-700 first-letter:float-left first-letter:mr-2 first-letter:font-serif first-letter:text-5xl first-letter:font-bold first-letter:text-secondary-600 first-letter:leading-[0.9]">
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-7 flex items-center justify-between rounded-lg bg-background-100 p-4">
                <span className="text-sm text-foreground-600">为你推荐的三个实用核对点已整理在上方深读中</span>
                <button
                  onClick={() => setReading(false)}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm font-bold text-background-50 transition hover:bg-foreground-800"
                >
                  读完收工
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}